import { User, Calendar, MapPin, Award, Target, TrendingUp } from 'lucide-react'
import { getTheme } from '../utils/colors'

function About({ config, isLight = false }) {
  const theme = getTheme(isLight, config.profile?.fontColor)
  const profile = config.profile
  const stats = config.stats

  return (
    <section id="about" className="py-20" style={{ backgroundColor: config.profile?.sectionBackground || '#000000' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${theme.text}`}>
            关于我
          </h2>
          <p className={theme.textSecondary}>
            用心创作，分享美好生活
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 左侧：个人介绍 */}
          <div>
            <div className={`rounded-2xl p-8 border ${theme.sectionBg} ${theme.cardBorder}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className={`text-xl font-semibold ${theme.text}`}>{profile.name}</h3>
                  <p className={theme.textSecondary}>{profile.tagline}</p>
                </div>
              </div>

              <p className={`leading-relaxed mb-6 ${theme.textSecondary}`}>
                {profile.bio}
              </p>

              <div className="space-y-3">
                <div className={`flex items-center gap-3 ${theme.textSecondary}`}>
                  <MapPin className={`w-5 h-5 text-accent ${theme.textSecondary}`} />
                  <span>{profile.location}</span>
                </div>
                <div className={`flex items-center gap-3 ${theme.textSecondary}`}>
                  <Calendar className={`w-5 h-5 text-accent ${theme.textSecondary}`} />
                  <span>从事内容创作 5 年</span>
                </div>
                <div className={`flex items-center gap-3 ${theme.textSecondary}`}>
                  <Award className={`w-5 h-5 text-accent ${theme.textSecondary}`} />
                  <span>累计合作品牌 {stats?.brandCollabs || 0}+</span>
                </div>
              </div>

              {/* 创作领域 */}
              <div className={`mt-6 pt-6 border-t ${theme.divider}`}>
                <h4 className={`text-sm font-medium mb-3 ${theme.textSecondary}`}>创作领域</h4>
                <div className="flex flex-wrap gap-2">
                  {profile.niche?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-accent/20 text-accent/70 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* 核心数据 */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className={`rounded-xl p-6 border text-center ${theme.sectionBg} ${theme.cardBorder}`}>
                <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <div className={`text-3xl font-bold ${theme.text}`}>95%</div>
                <div className={`text-sm ${theme.textSecondary}`}>客户满意度</div>
              </div>
              <div className={`rounded-xl p-6 border text-center ${theme.sectionBg} ${theme.cardBorder}`}>
                <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className={`text-3xl font-bold ${theme.text}`}>50w+</div>
                <div className={`text-sm ${theme.textSecondary}`}>平均作品播放</div>
              </div>
            </div>
          </div>

          {/* 右侧：合作愿景 + 优势 */}
          <div className="space-y-6">
            {/* 合作愿景 */}
            <div className={`rounded-2xl p-8 border ${theme.gradientBg} ${theme.gradientBorder}`}>
              <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>合作愿景</h3>
              <p className={`leading-relaxed mb-6 ${theme.textSecondary}`}>
                期待与更多优质品牌合作，通过创意内容帮助品牌触达目标用户，实现双赢。无论是产品推广、品牌曝光还是销售转化，都能提供专业的解决方案。
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className={`text-sm ${theme.textSecondary}`}>真实体验分享</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className={`text-sm ${theme.textSecondary}`}>精准用户触达</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className={`text-sm ${theme.textSecondary}`}>专业内容创作</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className={`text-sm ${theme.textSecondary}`}>数据效果追踪</span>
                </div>
              </div>
            </div>

            {/* 核心优势 */}
            <div className={`rounded-2xl p-8 border ${theme.sectionBg} ${theme.cardBorder}`}>
              <h3 className={`text-xl font-semibold mb-6 ${theme.text}`}>为什么选择我</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-accent/70 font-bold">1</span>
                  </div>
                  <div>
                    <h4 className={`font-medium mb-1 ${theme.text}`}>多平台矩阵覆盖</h4>
                    <p className={`text-sm ${theme.textSecondary}`}>抖音、小红书、B站、快手、西瓜视频全平台运营，一次创作多平台分发</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent/70 font-bold">2</span>
                  </div>
                  <div>
                    <h4 className={`font-medium mb-1 ${theme.text}`}>垂直领域深耕</h4>
                    <p className={`text-sm ${theme.textSecondary}`}>专注生活方式、美食探店、旅行攻略领域，粉丝画像精准匹配</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent/70 font-bold">3</span>
                  </div>
                  <div>
                    <h4 className={`font-medium mb-1 ${theme.text}`}>高互动粉丝群体</h4>
                    <p className={`text-sm ${theme.textSecondary}`}>粉丝粘性强，互动率高，种草转化率行业领先</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent/70 font-bold">4</span>
                  </div>
                  <div>
                    <h4 className={`font-medium mb-1 ${theme.text}`}>专业服务保障</h4>
                    <p className={`text-sm ${theme.textSecondary}`}>从创意策划到内容制作，全程专业对接，确保项目顺利交付</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About
